import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Plus, Trash2, Pencil, Copy, Search, Hexagon, Route, MapPin, Layers, ChevronDown, ChevronRight, MoreVertical,
} from 'lucide-react';
import { formatArea, groupItems } from './GeofencingUtils';
import { placeIconUrl } from './PlaceIconPicker';
import { resolveGroupTextColor } from '../shared/groupColors';
import { useIsMobileOrWebview } from '../../hooks/useIsMobileOrWebview';

// Shared "⋮" overflow actions menu — same pattern as the People page rows
// (52px touch targets + larger trigger on mobile/WebView).
const GeofenceItemActions = ({ kind, id, mob, onEdit, onDelete, onDuplicate }) => {
  const itemCls = mob ? 'min-h-[52px] text-sm px-3 py-3' : '';
  const iconCls = mob ? '!w-5 !h-5 mr-3' : 'w-4 h-4 mr-2';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className={mob ? 'h-9 w-9 p-0' : 'h-7 w-7 p-0'}
          title="Actions"
          onClick={(e) => e.stopPropagation()}
          data-testid={`${kind}-actions-menu-${id}`}
        >
          <MoreVertical className={mob ? '!w-5 !h-5' : 'w-4 h-4'} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={mob ? 'w-64 p-1.5' : 'w-44'} onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem className={itemCls} onSelect={onEdit} data-testid={`edit-${kind}-${id}`}>
          <Pencil className={iconCls} /> Edit
        </DropdownMenuItem>
        <DropdownMenuItem className={itemCls} onSelect={onDuplicate} data-testid={`duplicate-${kind}-${id}`}>
          <Copy className={iconCls} /> Clone
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className={`${itemCls} text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400`} onSelect={onDelete} data-testid={`delete-${kind}-${id}`}>
          <Trash2 className={iconCls} /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ZoneListItem = ({ zone, mob, selected, checked, onToggleCheck, onSelect, onEdit, onDelete, onDuplicate }) => (
  <div
    data-testid={`zone-item-${zone.id}`}
    className={`mx-1 rounded-md px-2 ${mob ? 'py-2.5' : 'py-2'} cursor-pointer transition-colors ${selected ? 'border border-[#2563eb] bg-[#2563eb]/10' : 'hover:bg-muted'}`}
    onClick={onSelect}
  >
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        <Checkbox
          checked={checked}
          onCheckedChange={() => onToggleCheck(zone.id)}
          onClick={(e) => e.stopPropagation()}
          className={`shrink-0 ${mob ? 'h-5 w-5' : ''}`}
          data-testid={`select-zone-${zone.id}`}
        />
        <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: zone.zone_color }} data-testid={`zone-color-${zone.id}`} />
        <div className="min-w-0">
          <p className={`${mob ? 'text-sm' : 'text-xs'} font-medium truncate`} data-testid={`zone-name-${zone.id}`}>{zone.zone_name}</p>
          <p className={`${mob ? 'text-xs' : 'text-[10px]'} text-muted-foreground capitalize`} data-testid={`zone-meta-${zone.id}`}>
            {zone.zone_type}
            {zone.area_size ? ` · ${formatArea(zone.area_size)}` : ''}
          </p>
        </div>
      </div>
      <div className="flex shrink-0">
        <GeofenceItemActions kind="zone" id={zone.id} mob={mob} onEdit={onEdit} onDelete={onDelete} onDuplicate={onDuplicate} />
      </div>
    </div>
  </div>
);

const PlaceListItem = ({ place, mob, selected, checked, onToggleCheck, onSelect, onEdit, onDelete, onDuplicate }) => (
  <div
    data-testid={`place-item-${place.id}`}
    className={`mx-1 rounded-md px-2 ${mob ? 'py-2.5' : 'py-2'} cursor-pointer transition-colors ${selected ? 'border border-[#2563eb] bg-[#2563eb]/10' : 'hover:bg-muted'}`}
    onClick={onSelect}
  >
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        <Checkbox
          checked={checked}
          onCheckedChange={() => onToggleCheck(place.id)}
          onClick={(e) => e.stopPropagation()}
          className={`shrink-0 ${mob ? 'h-5 w-5' : ''}`}
          data-testid={`select-place-${place.id}`}
        />
        {place.icon ? (
          <img src={placeIconUrl(place.icon)} alt="" className="w-5 h-5 shrink-0" data-testid={`place-color-${place.id}`} />
        ) : (
          <div className="w-3 h-3 rounded-full shrink-0 bg-emerald-500" data-testid={`place-color-${place.id}`} />
        )}
        <div className="min-w-0">
          <p className={`${mob ? 'text-sm' : 'text-xs'} font-medium truncate`} data-testid={`place-name-${place.id}`}>{place.place_name}</p>
          <p className={`${mob ? 'text-xs' : 'text-[10px]'} text-muted-foreground truncate`} data-testid={`place-meta-${place.id}`}>
            {place.radius}m
          </p>
        </div>
      </div>
      <div className="flex shrink-0">
        <GeofenceItemActions kind="place" id={place.id} mob={mob} onEdit={onEdit} onDelete={onDelete} onDuplicate={onDuplicate} />
      </div>
    </div>
  </div>
);

const RouteListItem = ({ route, mob, selected, checked, onToggleCheck, onSelect, onEdit, onDelete, onDuplicate }) => (
  <div
    data-testid={`route-item-${route.id}`}
    className={`mx-1 rounded-md px-2 ${mob ? 'py-2.5' : 'py-2'} cursor-pointer transition-colors ${selected ? 'border border-[#2563eb] bg-[#2563eb]/10' : 'hover:bg-muted'}`}
    onClick={onSelect}
  >
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        <Checkbox
          checked={checked}
          onCheckedChange={() => onToggleCheck(route.id)}
          onClick={(e) => e.stopPropagation()}
          className={`shrink-0 ${mob ? 'h-5 w-5' : ''}`}
          data-testid={`select-route-${route.id}`}
        />
        <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: route.route_color }} data-testid={`route-color-${route.id}`} />
        <div className="min-w-0">
          <p className={`${mob ? 'text-sm' : 'text-xs'} font-medium truncate`} data-testid={`route-name-${route.id}`}>{route.route_name}</p>
          <p className={`${mob ? 'text-xs' : 'text-[10px]'} text-muted-foreground`} data-testid={`route-meta-${route.id}`}>
            {route.path_coordinates.length} pts · {route.deviation_distance}m dev
          </p>
        </div>
      </div>
      <div className="flex shrink-0">
        <GeofenceItemActions kind="route" id={route.id} mob={mob} onEdit={onEdit} onDelete={onDelete} onDuplicate={onDuplicate} />
      </div>
    </div>
  </div>
);

const GroupSection = ({ groupKey, groupName, itemCount, groupColor, collapsed, onToggle, children }) => (
  <div>
    <button
      className="w-full sticky top-0 z-10 flex items-center gap-1.5 bg-slate-200 dark:bg-slate-700 px-2 py-1.5 text-xs font-semibold hover:bg-slate-300 dark:hover:bg-slate-600"
      onClick={onToggle}
      data-testid={`group-header-${groupKey}`}
      type="button"
    >
      {collapsed ? <ChevronRight className="w-3 h-3 text-slate-700 dark:text-slate-200" /> : <ChevronDown className="w-3 h-3 text-slate-700 dark:text-slate-200" />}
      <Layers className="w-3 h-3" style={groupColor ? { color: resolveGroupTextColor(groupColor) } : undefined} />
      <span className="truncate" style={groupColor ? { color: resolveGroupTextColor(groupColor) } : { color: 'inherit' }}>{groupName}</span>
      <span className="ml-auto text-[10px] font-normal text-slate-700 dark:text-slate-200" data-testid={`group-count-${groupKey}`}>{itemCount}</span>
    </button>
    {!collapsed ? children : null}
  </div>
);

export const GeofencingSidebar = ({
  activeTab,
  inlineEditor,
  searchQuery,
  onSearchChange,
  zones,
  places,
  routes,
  groups,
  selectedZone,
  selectedPlace,
  selectedRoute,
  collapsedGroups,
  onToggleGroup,
  onOpenAddZone,
  onOpenAddPlace,
  onOpenAddRoute,
  onSelectZone,
  onSelectPlace,
  onSelectRoute,
  onEditZone,
  onEditPlace,
  onEditRoute,
  onDeleteZone,
  onDeletePlace,
  onDeleteRoute,
  onDuplicateZone,
  onDuplicatePlace,
  onDuplicateRoute,
  showZonesLayer,
  onShowZonesLayerChange,
  showPlacesLayer,
  onShowPlacesLayerChange,
  showRoutesLayer,
  onShowRoutesLayerChange,
  selectedZoneIds,
  selectedPlaceIds,
  selectedRouteIds,
  onToggleSelectItem,
  onToggleSelectAll,
}) => {
  const mob = useIsMobileOrWebview();
  const filteredZones = useMemo(
    () => zones.filter((zone) => zone.zone_name.toLowerCase().includes(searchQuery.toLowerCase())),
    [zones, searchQuery],
  );
  const filteredPlaces = useMemo(
    () => places.filter((place) => place.place_name.toLowerCase().includes(searchQuery.toLowerCase())),
    [places, searchQuery],
  );
  const filteredRoutes = useMemo(
    () => routes.filter((route) => route.route_name.toLowerCase().includes(searchQuery.toLowerCase())),
    [routes, searchQuery],
  );

  const zoneSections = useMemo(() => groupItems(filteredZones, 'zone_group', groups, 'zone_name'), [filteredZones, groups]);
  const placeSections = useMemo(() => groupItems(filteredPlaces, 'place_group', groups, 'place_name'), [filteredPlaces, groups]);
  const routeSections = useMemo(() => groupItems(filteredRoutes, 'route_group', groups, 'route_name'), [filteredRoutes, groups]);

  return (
    <div className="h-full w-full shrink-0 min-h-0 flex flex-col gap-3 overflow-y-auto pr-1 lg:w-72" data-testid="geofencing-sidebar">
      {inlineEditor ? (
        <div className="flex-1 min-h-0 overflow-hidden" data-testid="inline-geofence-editor-wrap">
          {inlineEditor}
        </div>
      ) : null}

      {!inlineEditor ? (
      <Card className="flex-1 min-h-0 overflow-hidden flex flex-col" data-testid="geofence-list-card">
        <CardHeader className="border-b pl-4 pr-3 py-2 shrink-0">
          <div className="flex items-center gap-2 h-6" data-testid={`${activeTab}-selection-bar`}>
            <Checkbox
              checked={
                activeTab === 'zones' ? selectedZoneIds.length === zones.length && zones.length > 0
                  : activeTab === 'places' ? selectedPlaceIds.length === places.length && places.length > 0
                    : selectedRouteIds.length === routes.length && routes.length > 0
              }
              onCheckedChange={onToggleSelectAll}
              className="shrink-0"
              data-testid="select-all-checkbox"
            />
            {(() => {
              const total = activeTab === 'zones' ? zones.length : activeTab === 'places' ? places.length : routes.length;
              const count = activeTab === 'zones' ? selectedZoneIds.length : activeTab === 'places' ? selectedPlaceIds.length : selectedRouteIds.length;
              return (
                <div className="flex items-center gap-3 text-sm text-muted-foreground overflow-hidden">
                  <span data-testid={`${activeTab}-selection-count`}>{count} selected</span>
                  {count > 0 && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => onToggleSelectAll(count < total)}
                        data-testid={`${activeTab}-select-all-toggle`}
                      >
                        {count >= total ? 'Deselect All' : 'Select All'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => onToggleSelectAll(false)}
                        data-testid={`${activeTab}-selection-clear`}
                      >
                        Clear
                      </Button>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        </CardHeader>

        <CardContent className="p-0 flex-1 min-h-0 overflow-hidden">
          <ScrollArea className="h-full" data-testid="geofence-list-scroll-area">
            <div className="p-1 space-y-0.5">
              {activeTab === 'zones' ? (
                filteredZones.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground" data-testid="empty-zones-state">
                    <Hexagon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>No zones yet</p>
                    <p className="text-xs mt-1">Click &quot;Add Zone&quot; to create one</p>
                  </div>
                ) : (
                  <>
                    {zoneSections.sortedGroups.map((groupName) => (
                      <GroupSection
                        key={groupName}
                        groupKey={`zone-${groupName}`}
                        groupName={groupName}
                        itemCount={zoneSections.grouped[groupName].items.length}
                        groupColor={zoneSections.grouped[groupName].color}
                        collapsed={collapsedGroups.has(`zone-${groupName}`)}
                        onToggle={() => onToggleGroup(`zone-${groupName}`)}
                      >
                        {zoneSections.grouped[groupName].items.map((zone) => (
                          <ZoneListItem
                            key={zone.id}
                            zone={zone}
                            mob={mob}
                            selected={selectedZone?.id === zone.id}
                            checked={selectedZoneIds.includes(zone.id)}
                            onToggleCheck={onToggleSelectItem}
                            onSelect={() => onSelectZone(zone)}
                            onEdit={() => onEditZone(zone)}
                            onDelete={() => onDeleteZone(zone.id)}
                            onDuplicate={() => onDuplicateZone(zone)}
                          />
                        ))}
                      </GroupSection>
                    ))}
                  </>
                )
              ) : null}

              {activeTab === 'places' ? (
                filteredPlaces.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground" data-testid="empty-places-state">
                    <MapPin className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>No places yet</p>
                    <p className="text-xs mt-1">Click &quot;Add Place&quot; to create one</p>
                  </div>
                ) : (
                  <>
                    {placeSections.sortedGroups.map((groupName) => (
                      <GroupSection
                        key={groupName}
                        groupKey={`place-${groupName}`}
                        groupName={groupName}
                        itemCount={placeSections.grouped[groupName].items.length}
                        groupColor={placeSections.grouped[groupName].color}
                        collapsed={collapsedGroups.has(`place-${groupName}`)}
                        onToggle={() => onToggleGroup(`place-${groupName}`)}
                      >
                        {placeSections.grouped[groupName].items.map((place) => (
                          <PlaceListItem
                            key={place.id}
                            place={place}
                            mob={mob}
                            selected={selectedPlace?.id === place.id}
                            checked={selectedPlaceIds.includes(place.id)}
                            onToggleCheck={onToggleSelectItem}
                            onSelect={() => onSelectPlace(place)}
                            onEdit={() => onEditPlace(place)}
                            onDelete={() => onDeletePlace(place.id)}
                            onDuplicate={() => onDuplicatePlace(place)}
                          />
                        ))}
                      </GroupSection>
                    ))}
                  </>
                )
              ) : null}

              {activeTab === 'routes' ? (
                filteredRoutes.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground" data-testid="empty-routes-state">
                    <Route className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>No routes yet</p>
                    <p className="text-xs mt-1">Click &quot;Add Route&quot; to create one</p>
                  </div>
                ) : (
                  <>
                    {routeSections.sortedGroups.map((groupName) => (
                      <GroupSection
                        key={groupName}
                        groupKey={`route-${groupName}`}
                        groupName={groupName}
                        itemCount={routeSections.grouped[groupName].items.length}
                        groupColor={routeSections.grouped[groupName].color}
                        collapsed={collapsedGroups.has(`route-${groupName}`)}
                        onToggle={() => onToggleGroup(`route-${groupName}`)}
                      >
                        {routeSections.grouped[groupName].items.map((route) => (
                          <RouteListItem
                            key={route.id}
                            route={route}
                            mob={mob}
                            selected={selectedRoute?.id === route.id}
                            checked={selectedRouteIds.includes(route.id)}
                            onToggleCheck={onToggleSelectItem}
                            onSelect={() => onSelectRoute(route)}
                            onEdit={() => onEditRoute(route)}
                            onDelete={() => onDeleteRoute(route.id)}
                            onDuplicate={() => onDuplicateRoute(route)}
                          />
                        ))}
                      </GroupSection>
                    ))}
                  </>
                )
              ) : null}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
      ) : null}

    </div>
  );
};